import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { TaxScenario, TaxScenarioInput } from '@/tax/types';
import { useAuth } from '@/hooks/useAuth';
import { hidesPersonalClients, carriesClientBook } from '@/lib/clientBook';
import { useSandboxStore } from '@/hooks/useSandboxStore';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/safeSupabase';
import { toast as sonnerToast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronLeft, Play, Trash2 } from 'lucide-react';
import { formatCompactCurrency } from '@/tax/format';
import AppSidebar, { type AppView } from '@/components/AppSidebar';
import ScenarioWizard, { clearDraft, type WizardSubmitPayload } from '@/components/ScenarioWizard';
import DashboardView from '@/components/DashboardView';
import MyClientsGrid from '@/components/MyClientsGrid';
import ResultsDisplay from '@/components/ResultsDisplay';
import ComparisonTable from '@/components/ComparisonTable';
import AddStrategySheet from '@/components/AddStrategySheet';
import TeamManagement from '@/components/org/TeamManagement';
import CaseAssignments from '@/components/org/CaseAssignments';
import SettingsPage from '@/components/SettingsPage';
import { variantToInput, variantAutoName, scenarioToVariant, resolveVariantSuffix, type StrategyVariant } from '@/components/wizard/steps/StrategyBuilderStep';
import { WIZARD_STEPS } from '@/components/wizard/types';
import { calculateTaxServer } from '@/tax/taxApi';
import { TutorialProvider } from '@/tutorial/TutorialContext';
import WalkthroughBanner from '@/tutorial/WalkthroughBanner';
import { buildSampleClients, SAMPLE_NOTICE, SAMPLE_WIZARD_CLIENT, sampleWizardVariants, sampleAdvisors } from '@/tutorial/sampleData';
import { fullTourFor, walkthroughVisible, WALKTHROUGHS, type Walkthrough } from '@/tutorial/content';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { logAudit as _logAudit } from '@/lib/audit';
import { isSafeRolloverPoint, rolloverIfUpdatePending } from '@/lib/deployWatcher';
import { scheduleChunkWarmup } from '@/lib/warmChunks';
import { useDelayedDelete } from '@/hooks/useDelayedDelete';

/** Strip any trailing parenthetical to get the base client name */
function getClientGroup(name: string): string {
  return name.replace(/\s*\(.*\)$/, '').trim();
}

/** Every client list a delete can show up in: My Clients, Drafts, All Clients. */
const CLIENT_LIST_KEYS = [['scenarios'], ['drafts'], ['org-scenarios']] as const;

export default function Index() {
  const { user, profile, isSuperAdmin, isPlatformAdmin, isOrgOwner, isOrgAdmin, org, loading } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  const realStore = useSandboxStore();
  const location = useLocation();

  // Demo mode — Team Management passes mock scenarios via router state so the
  // real DashboardView / wizard / comparison renders with populated data.
  // initialView: Team Management's sidebar hands its target view over here
  // (its nav items would otherwise no-op — Index owns the view state).
  const locState = location.state as { demoScenarios?: TaxScenario[]; demoClientId?: string; initialView?: AppView } | null;
  const [demoScenarios, setDemoScenarios] = useState<TaxScenario[]>(() => locState?.demoScenarios ?? []);
  const [demoClientId, setDemoClientId] = useState<string | null>(() => locState?.demoClientId ?? null);
  const isDemoMode = demoScenarios.length > 0;

  // In demo mode, all store mutations operate on the in-memory demoScenarios
  // array instead of hitting Supabase. Results are reused from existing mock data.
  const demoCounter = useRef(0);
  const demoId = () => `demo-${Date.now()}-${++demoCounter.current}`;

  const store: typeof realStore = isDemoMode ? {
    isSandbox: false as const,
    sandboxVersion: -1,
    loadScenarios: async () => demoScenarios,
    loadDrafts: async () => [],
    createScenario: async (input: TaxScenarioInput, userId: string, clientId?: string) => {
      const template = demoScenarios[0];
      const newScenario: TaxScenario = {
        id: demoId(),
        clientId: clientId ?? demoId(),
        rootClientId: clientId ?? demoScenarios.find(s => s.clientId === clientId)?.rootClientId ?? demoId(),
        input,
        results: { ...template.results },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isDraft: false,
        sortOrder: clientId
          ? Math.max(0, ...demoScenarios.filter(s => s.clientId === clientId).map(s => s.sortOrder ?? 0)) + 1
          : 0,
        userId,
      };
      setDemoScenarios(prev => [...prev, newScenario]);
      return newScenario;
    },
    updateScenario: async (id: string, input: TaxScenarioInput) => {
      let updated: TaxScenario | null = null;
      setDemoScenarios(prev => prev.map(s => {
        if (s.id !== id) return s;
        updated = { ...s, input, updatedAt: new Date().toISOString() };
        return updated;
      }));
      if (!updated) {
        updated = { ...demoScenarios[0], id, input, updatedAt: new Date().toISOString() };
      }
      return updated;
    },
    deleteScenario: async (id: string) => {
      setDemoScenarios(prev => prev.filter(s => s.id !== id));
    },
    duplicateScenario: async (scenario: TaxScenario, userId: string) => {
      const newScenario: TaxScenario = {
        ...scenario,
        id: demoId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId,
        input: { ...scenario.input, name: scenario.input.name + ' (copy)' },
      };
      setDemoScenarios(prev => [...prev, newScenario]);
      return newScenario;
    },
    saveDraftScenario: async (input: TaxScenarioInput, userId: string) => {
      const draft: TaxScenario = {
        id: demoId(), clientId: demoId(), rootClientId: demoId(),
        input, results: { ...demoScenarios[0].results },
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
        isDraft: true, sortOrder: 0, userId,
      };
      return draft;
    },
    updateDraftScenario: async (id: string, input: TaxScenarioInput) => {
      const existing = demoScenarios.find(s => s.id === id) ?? demoScenarios[0];
      return { ...existing, id, input, updatedAt: new Date().toISOString() };
    },
    finalizeDraft: async (id: string, input: TaxScenarioInput) => {
      const template = demoScenarios[0];
      const finalized: TaxScenario = {
        id, clientId: demoId(), rootClientId: demoId(),
        input, results: { ...template.results },
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
        isDraft: false, sortOrder: 0, userId: template.userId,
      };
      setDemoScenarios(prev => [...prev, finalized]);
      return finalized;
    },
    reorderScenarios: async (ids: string[], orders: number[]) => {
      setDemoScenarios(prev => prev.map(s => {
        const idx = ids.indexOf(s.id);
        return idx >= 0 ? { ...s, sortOrder: orders[idx] } : s;
      }));
    },
  } : realStore;

  // Clear router state after capturing it so a browser refresh doesn't replay.
  // Intentionally one-shot: locState and navigate are captured at mount time.
  useEffect(() => {
    if (locState?.demoScenarios || locState?.initialView) {
      navigate('/', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A client opened from Team Management lives in the in-memory viewing layer.
  // When impersonation ENDS that context is gone, so drop it — otherwise the
  // stale client keeps the super-admin redirect below suppressed and strands
  // the admin on a regular-app page that shouldn't exist for them.
  const wasImpersonatingRef = useRef(isImpersonating);
  useEffect(() => {
    if (wasImpersonatingRef.current && !isImpersonating) {
      setDemoScenarios([]);
      setDemoClientId(null);
    }
    wasImpersonatingRef.current = isImpersonating;
  }, [isImpersonating]);

  // Super admins have no business in the regular app — redirect to the admin panel.
  // isSuperAdmin is the EFFECTIVE role: while impersonating it reflects the
  // impersonated user, so impersonating a non-super-admin correctly lands on
  // their home page, and exiting re-evaluates and bounces back to the panel.
  // (Checking !isImpersonating here would defeat both.) The demo-data guard
  // stays: a client opened from Team Management must not be bounced away.
  useEffect(() => {
    if (!loading && isSuperAdmin && !isDemoMode) navigate('/super-admin', { replace: true });
  }, [loading, isSuperAdmin, isDemoMode, navigate]);

  const [currentView, setCurrentView] = useState<AppView>(() =>
    locState?.initialView ?? (locState?.demoClientId ? 'client-view' : 'dashboard'),
  );

  // Guided-tutorial practice mode: the wizard runs for real (server-side
  // calculation included) but submissions build ephemeral scenarios instead of
  // saving. Entered from the tutorial welcome dialog; exited via the banner.
  const [practiceMode, setPracticeMode] = useState(false);
  const [practiceScenarios, setPracticeScenarios] = useState<TaxScenario[]>([]);
  // Save & Calculate in practice mode asks first: save for real, or keep practicing.
  // Sample clients: made up for a walkthrough when the person has none of their
  // own, held in the in-memory demo store, gone when the walkthrough ends.
  const [sampleClientsActive, setSampleClientsActive] = useState(false);

  const exitPractice = () => {
    setPracticeMode(false);
    setPracticeScenarios([]);
    setLastResults(null);
    setEditingId(null);
    setActiveDraftId(null);
    setDraftInitial(null);
    setWizardStep(0);
    setVisitedSteps(new Set());
    setCreateForUserId(null);
    setCurrentView('dashboard');
  };

  // A support admin (Dena, Veronica) has no My Clients page — see
  // hidesPersonalClients(). The initial view is chosen before the profile has
  // loaded, and six different fallbacks reset to 'dashboard', so guard the
  // destination continuously instead of patching each call site. All Clients
  // is their home: creating from there forces the advisor picker, so the row
  // is owned by the right advisor at save time.
  const noClientBook = hidesPersonalClients({
    hasClientBook: profile?.has_client_book,
    isSuperAdmin,
    isOrgOwner,
    isOrgAdmin,
  });
  React.useEffect(() => {
    if (noClientBook && currentView === 'dashboard') setCurrentView('org-scenarios');
  }, [noClientBook, currentView]);

  // A newer build deployed while this tab was open is applied when the advisor
  // arrives at one of the standing-still destinations (see
  // SAFE_ROLLOVER_VIEWS) — a reload there lands them exactly where they were
  // going. Never spontaneously, never mid-wizard, never on results/compare or
  // inside a client folder. For a support admin 'dashboard' is the transient
  // redirect stop above rather than a place they navigated to, so it doesn't
  // count for them — 'org-scenarios', where they actually land, does.
  useEffect(() => {
    if (noClientBook && currentView === 'dashboard') return;
    if (isSafeRolloverPoint({ view: currentView, practiceMode, isDemoMode, isImpersonating })) {
      rolloverIfUpdatePending();
    }
  }, [noClientBook, currentView, practiceMode, isDemoMode, isImpersonating]);

  // Pull the 1040 importer's PDF reader into this tab during idle time so a
  // later deploy can't break the import for a tab that's already open.
  useEffect(() => scheduleChunkWarmup(), []);

  React.useEffect(() => {
    // Settings is deliberately absent — every role has one, gated by section.
    const orgViews: AppView[] = ['team', 'org-scenarios', 'assignments'];
    const canManage = isSuperAdmin || isOrgOwner || isOrgAdmin;
    if (orgViews.includes(currentView) && !canManage) {
      setCurrentView('dashboard');
    }
  }, [currentView, isSuperAdmin, isOrgOwner, isOrgAdmin]);


  // Scroll behavior on view change:
  //  - "Back" actions (in-view back buttons) restore the scroll position the
  //    user left at, so going back to dashboard after viewing client #14 lands
  //    them at client #14 again.
  //  - Everything else (sidebar nav, View Results, Save & Calculate, etc.)
  //    resets to top, since the user is initiating a fresh view.
  const scrollPositions = useRef<Partial<Record<AppView, number>>>({});
  const isBackNavRef = useRef(false);
  // Track scroll position continuously per view. Saving in a view-change
  // effect doesn't work — by the time that effect fires, the old view's DOM
  // is already gone and window.scrollY reads as 0. Instead, every scroll
  // event saves the position for whichever view is currently active.
  React.useEffect(() => {
    const handler = () => {
      scrollPositions.current[currentView] = window.scrollY;
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, [currentView]);
  React.useEffect(() => {
    const targetY = isBackNavRef.current
      ? (scrollPositions.current[currentView] ?? 0)
      : 0;
    window.scrollTo({ top: targetY, behavior: 'auto' });
    isBackNavRef.current = false;
  }, [currentView]);
  const goBackTo = (view: AppView) => {
    isBackNavRef.current = true;
    setCurrentView(view);
  };

  // Focused single-client "Client View" page (client-facing presentation — no other client names).
  const [clientViewId, setClientViewId] = useState<string | null>(() => locState?.demoClientId ?? null);
  // Which list view the user was on when they entered Client View. Drives the back button —
  // Client View opened from the All Clients grid returns there; from a lineage row-list returns there.
  const [clientViewSourceView, setClientViewSourceView] = useState<AppView>('dashboard');
  const handleClientView = (clientId: string) => {
    setClientViewId(clientId);
    setClientViewSourceView(currentView);
    setCurrentView('client-view');
  };
  // When the user clicks a multi-version folder on the My Clients OR All Clients grid, the dashboard
  // renders the existing row-list scoped to that lineage's clients. Back from the row-list returns
  // to whichever grid the user was on; back from Client View returns to the row-list when this is set.
  const [viewedLineageId, setViewedLineageId] = useState<string | null>(null);
  // Accordion expand state for the My Clients dashboard, lifted here so it survives navigating
  // to a comparison/results view and back.
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const toggleClientExpanded = useCallback((clientId: string) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);
  // Which list view "Back" returns to from a comparison/results page. Tracks the last
  // list view the user was on, so launching Compare from Client View returns to Client View.
  const [scopedReturnView, setScopedReturnView] = useState<AppView>('dashboard');
  React.useEffect(() => {
    if (currentView === 'dashboard' || currentView === 'client-view' || currentView === 'org-scenarios') {
      setScopedReturnView(currentView);
    }
  }, [currentView]);
  // A brand-new client was just saved from the wizard. Point "Back" on the first
  // results/comparison page at that client's own folder (Client View) instead of
  // the full My Clients grid, which shows every client the advisor has. Client
  // View's own Back then returns to the grid. Must run before the view switches to
  // results/compare — scopedReturnView only self-tracks on list views.
  // Client View's own Back returns to the grid the wizard was launched from
  // (My Clients or All Clients) — never My Clients by default. Launched from
  // another client's Client View, it falls through to that view's own source grid.
  const focusNewClient = (clientId: string) => {
    setClientViewId(clientId);
    if (scopedReturnView !== 'client-view') setClientViewSourceView(scopedReturnView);
    setScopedReturnView('client-view');
  };

  const [wizardStep, setWizardStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set());
  // React `key` for the mounted ScenarioWizard. The wizard holds the user's
  // in-progress input in its own state and reads `initial` ONLY at mount, so a
  // key change mid-session throws away everything typed since. This counter is
  // bumped ONLY when a genuinely new wizard session starts (new client, edit a
  // baseline, resume a draft) — never from the background draft auto-save,
  // which resolves at an unpredictable moment and used to remount the wizard
  // back to a stale snapshot, silently wiping income entered after it fired.
  const [wizardSessionId, setWizardSessionId] = useState(0);
  const beginWizardSession = () => setWizardSessionId(n => n + 1);
  // All Clients "New Client" with an advisor picked: the wizard's save writes
  // rows OWNED by this user (an org member), not the logged-in creator. null =
  // normal self-owned creation. Only ever set on the fresh-create path; edit
  // and draft flows never read it.
  const [createForUserId, setCreateForUserId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [lastResults, setLastResults] = useState<TaxScenario | null>(null);
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set());
  // Preserves the user-chosen order of scenarios in the comparison view.
  // Source of truth for column order; the first entry is the baseline.
  const [compareOrder, setCompareOrder] = useState<string[]>([]);
  const [compareBaselineId, setCompareBaselineId] = useState<string | null>(null);
  const [draftInitial, setDraftInitial] = useState<TaxScenarioInput | null>(null);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  // Add-strategy slide-over: holds the baseline scenario for the client being extended
  const [addStrategyBaseline, setAddStrategyBaseline] = useState<TaxScenario | null>(null);
  // Edit-strategy slide-over: holds the variant being edited plus its baseline (for live recalc).
  // Separate from editingId, which is reserved for baseline edits in the wizard.
  const [editingVariant, setEditingVariant] = useState<{ scenario: TaxScenario; baseline: TaxScenario } | null>(null);
  const autoSavingRef = useRef(false);
  const editOldNameRef = useRef('');
  const wizardSubmitRef = useRef<(() => void) | null>(null);
  const wizardSubmitCompareRef = useRef<(() => void) | null>(null);

  // CACHE KEY SCOPING — CRITICAL — DO NOT REMOVE user?.id (3rd recurrence of stale-cache bug)
  // Every query key for user-specific data MUST include the effective user ID.
  // Without it, logging out and into a different account serves cached data from
  // the previous user's session. React Query keys are the cache identity — if two
  // users share the same key, they share the same cache entry.
  // See CLAUDE.md rule #9: "Never break query cache key scoping."
  const scenariosKey = ['scenarios', store.isSandbox ? `sandbox-${impersonatedUserId}-${store.sandboxVersion}` : `live-${user?.id}`] as const;
  const draftsKey = ['drafts', store.isSandbox ? `sandbox-${impersonatedUserId}-${store.sandboxVersion}` : `live-${user?.id}`] as const;

  // A deleted row stays hidden on every list until its delete has landed.
  const delayedDelete = useDelayedDelete(queryClient, CLIENT_LIST_KEYS);
  const hidePendingDeletes = delayedDelete.hide;

  const { data: rawScenarios = [], isLoading } = useQuery({
    queryKey: scenariosKey,
    queryFn: store.loadScenarios,
  });
  const scenarios = React.useMemo(
    () => isDemoMode ? demoScenarios : hidePendingDeletes(rawScenarios),
    [rawScenarios, demoScenarios, isDemoMode, hidePendingDeletes],
  );

  const { data: rawDrafts = [] } = useQuery({
    queryKey: draftsKey,
    queryFn: store.loadDrafts,
  });
  const drafts = React.useMemo(() => hidePendingDeletes(rawDrafts), [rawDrafts, hidePendingDeletes]);

  // If the user is on the Drafts view and deletes/resumes their last draft, the
  // sidebar item disappears — bounce them back to My Clients so they aren't
  // stranded on an empty page with no nav. Placed AFTER `drafts` is declared
  // (the previous position caused a TDZ crash in the production minified build:
  // "Cannot access 'Kt' before initialization").
  React.useEffect(() => {
    if (currentView === 'drafts' && drafts.length === 0) {
      setCurrentView('dashboard');
    }
    // drafts identity changes on every refetch — only react to its length transitioning to 0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts.length, currentView]);

  // Org-wide scenarios. Drives the All Clients render AND seeds accessibleScenarios
  // so cross-user lookups (org manager editing an agent's client, etc.) keep working.
  // Drafts intentionally excluded — All Clients shows only completed scenarios.
  // The query stays enabled whenever the user can see the org (not gated to the route)
  // so handlers in wizard/compare/add flows can still resolve ids after navigating away.
  const canSeeOrg = isSuperAdmin || isOrgOwner || isOrgAdmin;
  const { data: rawOrgScenarios = [], isLoading: orgScenariosLoading } = useQuery({
    queryKey: ['org-scenarios', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('scenarios')
        .select('*')
        .eq('org_id', org.id)
        .eq('is_draft', false)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      const { rowToScenario } = await import('@/tax/scenarioStore');
      return (data ?? []).map(rowToScenario);
    },
    enabled: !!org && canSeeOrg,
  });
  const orgScenarios = React.useMemo(() => hidePendingDeletes(rawOrgScenarios), [rawOrgScenarios, hidePendingDeletes]);

  // Auto-bounce out of stranded views when their backing data is gone:
  //   - Client View: clientId no longer exists in scenarios
  //   - My Clients lineage row-list: viewedLineageId has zero members
  //   - All Clients lineage row-list: same against orgScenarios
  // Without these, deleting the last row from any of them leaves the user
  // staring at an empty-state card with no nav. Gated on isLoading (NOT
  // `scenarios.length === 0`) so deleting the user's LAST client still
  // triggers the bounce — the previous length-zero gate matched both "still
  // loading" AND "legitimately empty post-delete" and blocked the bounce in
  // the second case.
  React.useEffect(() => {
    if (currentView !== 'client-view' || !clientViewId) return;
    if (isLoading) return;
    // Cross-owner opens (All Clients → someone else's client) exist only in the
    // org pool, which loads on its own schedule — bouncing before it settles
    // ejected every teammate's client as "gone" (the 2026-08-31 demo bug).
    if (canSeeOrg && !!org && orgScenariosLoading) return;
    const exists = scenarios.some(s => s.clientId === clientViewId)
      || orgScenarios.some(s => s.clientId === clientViewId);
    if (!exists) {
      setClientViewId(null);
      setCurrentView(clientViewSourceView);
    }
  }, [currentView, clientViewId, clientViewSourceView, scenarios, orgScenarios, orgScenariosLoading, canSeeOrg, org, isLoading]);
  React.useEffect(() => {
    if (currentView !== 'dashboard' || !viewedLineageId) return;
    if (isLoading) return;
    const hasMembers = scenarios.some(s => s.rootClientId === viewedLineageId);
    if (!hasMembers) setViewedLineageId(null);
  }, [currentView, viewedLineageId, scenarios, isLoading]);
  React.useEffect(() => {
    if (currentView !== 'org-scenarios' || !viewedLineageId) return;
    if (!org || !canSeeOrg) return;
    const hasMembers = orgScenarios.some(s => s.rootClientId === viewedLineageId);
    if (!hasMembers) setViewedLineageId(null);
  }, [currentView, viewedLineageId, orgScenarios, org, canSeeOrg]);

  const { data: orgProfiles = [] } = useQuery({
    queryKey: ['org-profiles-lookup', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('profiles').select('id, full_name, email, status, has_client_book').eq('org_id', org.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!org && canSeeOrg,
  });

  // A deactivated advisor (20260929000002) is off every screen: not offered
  // as an advisor to start a client for, and their clients are kept but out
  // of sight until they're reactivated. Their name still resolves, so a
  // reactivated book reads as it always did.
  const activeAdvisors = React.useMemo(
    () => orgProfiles.filter(p => (p as { status?: string }).status !== 'deactivated'),
    [orgProfiles]);
  const deactivatedAdvisorIds = React.useMemo(
    () => new Set(orgProfiles.filter(p => (p as { status?: string }).status === 'deactivated').map(p => p.id)),
    [orgProfiles]);
  // While the walkthrough's sample clients are seeded they are the whole
  // book on All Clients too, since that is a support admin's home.
  const visibleOrgScenarios = React.useMemo(
    () => sampleClientsActive ? scenarios
      : (deactivatedAdvisorIds.size === 0 ? orgScenarios : orgScenarios.filter(s => !deactivatedAdvisorIds.has(s.userId))),
    [orgScenarios, deactivatedAdvisorIds, sampleClientsActive, scenarios]);
  const creatorById = React.useMemo(() => {
    const m = new Map<string, { full_name: string | null; email: string | null }>();
    for (const p of orgProfiles) m.set(p.id, { full_name: p.full_name, email: p.email });
    return m;
  }, [orgProfiles]);

  // Merged lookup pool: user's own scenarios + org-wide scenarios. `scenarios`
  // wins on id collision so optimistic updates to the user's own rows are reflected.
  // Used only for resolving ids in handlers — "My Work" (DashboardView) still
  // reads `scenarios` directly.
  const accessibleScenarios = React.useMemo(() => {
    const byId = new Map<string, TaxScenario>();
    for (const s of orgScenarios) byId.set(s.id, s);
    for (const s of scenarios) byId.set(s.id, s);
    return Array.from(byId.values());
  }, [scenarios, orgScenarios]);

  // Fetch assignments for agent dashboard
  const { data: myAssignments = [] } = useQuery({
    queryKey: ['my-assignments', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('case_assignments')
        .select('client_id')
        .eq('assigned_to', user.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !isSuperAdmin && !isOrgOwner && !isOrgAdmin,
  });

  const assignedClientIds = React.useMemo(() => {
    if (isSuperAdmin || isOrgOwner || isOrgAdmin) return undefined;
    return new Set(myAssignments.map(a => a.client_id));
  }, [myAssignments, isSuperAdmin, isOrgOwner, isOrgAdmin]);

  // Fetch orgs for super admin transfer
  const { data: orgsForTransfer = [] } = useQuery({
    queryKey: ['orgs-for-transfer'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('id, name, owner_id');
      if (error) throw error;
      return data ?? [];
    },
    enabled: isSuperAdmin,
  });

  const logAudit = (action: string, targetType?: string, targetId?: string, details?: Record<string, any>) => {
    // Suppress audit logging during sandbox impersonation
    if (store.isSandbox) return;
    _logAudit(action, org?.id, targetType, targetId, details);
  };

  const createMut = useMutation({
    mutationFn: async ({ input, clientId, draftIdToDelete }: { input: TaxScenarioInput; clientId?: string; draftIdToDelete?: string }) => {
      const scenario = await store.createScenario(input, user!.id, clientId);
      if (draftIdToDelete) {
        await store.deleteScenario(draftIdToDelete).catch(() => {});
      }
      return scenario;
    },
    onSuccess: async (scenario) => {
      if (assignedClientIds?.has(scenario.clientId)) {
        try {
          await supabase.rpc('claim_assigned_client', { p_client_id: scenario.clientId });
          queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
          logAudit('client.claimed', 'client', scenario.clientId, { client_name: scenario.input.name });
        } catch (err) {
          console.error('Failed to claim client:', err);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      queryClient.invalidateQueries({ queryKey: ['org-scenarios'] });
      logAudit('scenario.created', 'scenario', scenario.id, { scenario_name: scenario.input.name });
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: string; input: TaxScenarioInput }) => store.updateScenario(id, input),
    onSuccess: async (scenario) => {
      const oldBaseName = getClientGroup(editOldNameRef.current);
      const newBaseName = getClientGroup(scenario.input.name);
      if (oldBaseName && oldBaseName !== newBaseName) {
        // Pull siblings from the merged pool so an org manager editing an
        // agent's client can still rename the agent-owned variants.
        const siblings = accessibleScenarios
          .filter(s => s.clientId === scenario.clientId && s.id !== scenario.id);
        await Promise.all(siblings.map(sibling => {
          const sibBase = getClientGroup(sibling.input.name);
          if (sibBase === oldBaseName) {
            const suffix = sibling.input.name.slice(oldBaseName.length);
            return store.updateScenario(sibling.id, { ...sibling.input, name: newBaseName + suffix });
          }
          return Promise.resolve();
        }));
      }
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['org-scenarios'] });
      logAudit('scenario.updated', 'scenario', scenario.id, { scenario_name: scenario.input.name });
      setLastResults(scenario);
      setEditingId(null);
      setCurrentView('results');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const reorderMut = useMutation({
    mutationFn: ({ ids, orders }: { ids: string[]; orders: number[] }) =>
      store.reorderScenarios(ids, orders),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scenarios'] }),
    onError: (err: any) => toast.error('Reorder failed', { description: sanitizeError(err) }),
  });

  // Nickname a strategy scenario from the dashboard pencil. Pure rename — no
  // recalculation, results untouched. The new name arrives fully formed
  // ("Client (nickname)") from DashboardView, preserving the prefix convention
  // that strategySuffix/exports parse. Sandbox-aware via useSandboxStore, like
  // every other scenario mutation.
  const renameMut = useMutation({
    mutationFn: ({ scenario, newName }: { scenario: TaxScenario; newName: string }) =>
      store.renameScenario(scenario.id, { ...scenario.input, name: newName }),
    onSuccess: (scenario, { scenario: before }) => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['org-scenarios'] });
      // Distinct action from 'scenario.updated' (wizard/number edits) so the
      // audit log reads as what actually happened, with the before/after names.
      logAudit('scenario.renamed', 'scenario', scenario.id, {
        scenario_name: scenario.input.name,
        old_name: before.input.name,
        new_name: scenario.input.name,
      });
      toast.success('Renamed', { description: scenario.input.name });
    },
    onError: (err: any) => toast.error('Rename failed', { description: sanitizeError(err) }),
  });

  // Imperative bounce: called right after a delete optimistically mutates the cache.
  // Reads the freshly-filtered scenarios array (returned by setQueryData) so the
  // navigation happens in the same tick — no dependency on the post-render effect.
  // The effects below are still kept as a safety net for server-side invalidations.
  const bounceIfStranded = (next: TaxScenario[]) => {
    if (currentView === 'client-view' && clientViewId && !next.some(s => s.clientId === clientViewId)) {
      setClientViewId(null);
      setCurrentView(clientViewSourceView);
    }
    if (viewedLineageId && !next.some(s => s.rootClientId === viewedLineageId)) {
      setViewedLineageId(null);
    }
  };

  // Delayed delete with undo: hidden on every list at once, deleted when the
  // 5-second undo window runs out (useDelayedDelete).
  const handleDelete = useCallback((id: string) => {
    const scenario = scenarios.find(sc => sc.id === id) || drafts.find(d => d.id === id);
    const name = scenario?.input?.name || 'Scenario';

    const newScenarios = queryClient.setQueryData<TaxScenario[]>(scenariosKey, old => (old ?? []).filter(s => s.id !== id)) ?? [];
    queryClient.setQueryData<TaxScenario[]>(draftsKey, old => (old ?? []).filter(s => s.id !== id));
    bounceIfStranded(newScenarios);

    delayedDelete.schedule(id, [id], async () => {
      await store.deleteScenario(id);
      logAudit('scenario.deleted', 'scenario', id, { scenario_name: name });
    }, (err: unknown) => toast.error('Delete failed', { description: sanitizeError(err) }));

    sonnerToast(`"${name}" deleted`, {
      duration: 5000,
      action: { label: 'Undo', onClick: () => delayedDelete.undo(id) },
    });
  }, [scenarios, drafts, queryClient, toast, scenariosKey, draftsKey, currentView, clientViewId, clientViewSourceView, viewedLineageId, delayedDelete]);

  const handleDeleteMultiple = async (ids: string[]) => {
    const newScenarios = queryClient.setQueryData<TaxScenario[]>(scenariosKey, old => (old ?? []).filter(s => !ids.includes(s.id))) ?? [];
    queryClient.setQueryData<TaxScenario[]>(draftsKey, old => (old ?? []).filter(s => !ids.includes(s.id)));
    bounceIfStranded(newScenarios);

    const groupKey = `group-${ids.join(',')}`;
    delayedDelete.schedule(groupKey, ids, async () => {
      for (const id of ids) {
        const s = scenarios.find(sc => sc.id === id);
        await store.deleteScenario(id);
        if (s) logAudit('scenario.deleted', 'scenario', s.id, { scenario_name: s.input.name });
      }
    }, (err: unknown) => toast.error('Delete failed', { description: sanitizeError(err) }));

    sonnerToast(`${ids.length} scenario(s) deleted`, {
      duration: 5000,
      action: { label: 'Undo', onClick: () => delayedDelete.undo(groupKey) },
    });
  };

  const dupMut = useMutation({
    mutationFn: (s: TaxScenario) => store.duplicateScenario(s, user!.id),
    onSuccess: (newScenario) => {
      queryClient.setQueryData<TaxScenario[]>(scenariosKey, old =>
        old ? [...old, newScenario] : [newScenario]
      );
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Drag-to-merge two lineages on the personal My Clients grid. Every scenario
  // whose root_client_id matches dropRootId gets re-pointed to keepRootId,
  // collapsing them into one stacked folder. Sandbox-aware: cache-only when
  // impersonating, real UPDATE otherwise (RLS scopes to the user's own rows).
  const mergeLineagesMut = useMutation({
    mutationFn: async ({ keepRootId, dropRootIds }: { keepRootId: string; dropRootIds: string[] }) => {
      const drops = dropRootIds.filter(id => id && id !== keepRootId);
      if (drops.length === 0) return;
      if (isImpersonating) {
        const candidateQueries = queryClient.getQueryCache().getAll().filter(q => {
          const data = q.state.data;
          return Array.isArray(data) && data.length > 0
            && typeof (data[0] as { clientId?: unknown }).clientId === 'string';
        });
        const dropSet = new Set(drops);
        for (const q of candidateQueries) {
          const data = q.state.data as TaxScenario[];
          if (!data.some(s => dropSet.has(s.rootClientId))) continue;
          queryClient.setQueryData<TaxScenario[]>(
            q.queryKey as Parameters<typeof queryClient.setQueryData>[0],
            (old: TaxScenario[] | undefined) =>
              (old ?? []).map(s => (dropSet.has(s.rootClientId) ? { ...s, rootClientId: keepRootId } : s)),
          );
        }
        return;
      }
      const { error } = await supabase.from('scenarios')
        .update({ root_client_id: keepRootId })
        .in('root_client_id', drops);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      const drops = vars.dropRootIds.filter(id => id && id !== vars.keepRootId);
      logAudit('client.lineages_merged', 'client', vars.keepRootId, { dropped_roots: drops });
      if (!isImpersonating) {
        queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      }
      toast.success(`Merged${drops.length > 1 ? ` ${drops.length} folders` : ''}${isImpersonating ? ' (sandbox)' : ''}`);
    },
    onError: (err: unknown) => toast.error('Merge failed', { description: sanitizeError(err) }),
  });

  // Bulk-delete every scenario belonging to the selected lineages. Reuses the
  // optimistic + 5-second-undo pattern from handleDeleteMultiple so users can
  // recover from a fat-finger, even though the typed-"delete" confirm in the
  // dialog should already prevent accidents. The per-scenario 'scenario.deleted'
  // audits inside handleDeleteMultiple cover the audit trail — no separate
  // folder-level action is logged (would require an allowlist migration).
  const handleDeleteLineages = useCallback((rootIds: string[]) => {
    if (!rootIds.length) return;
    const rootSet = new Set(rootIds);
    const ids = scenarios.filter(s => rootSet.has(s.rootClientId)).map(s => s.id);
    if (!ids.length) return;
    handleDeleteMultiple(ids);
  // handleDeleteMultiple is recreated every render so we don't list it as a dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios]);

  const dupClientMut = useMutation({
    mutationFn: async ({ clientId, baselineOnly }: { clientId: string; clientName: string; baselineOnly: boolean }) => {
      // Demo mode: clone the client's scenarios in-memory (baseline only, or all)
      if (isDemoMode) {
        const sourceRows = demoScenarios.filter(s =>
          s.clientId === clientId && (!baselineOnly || (s.sortOrder ?? 0) === 0));
        const newClientId = demoId();
        const stamp = new Date().toISOString();
        const baseline = sourceRows.find(s => (s.sortOrder ?? 0) === 0) ?? sourceRows[0];
        const baseName = getClientGroup(baseline.input.name);
        const existing = new Set(demoScenarios.filter(s => (s.sortOrder ?? 0) === 0).map(s => getClientGroup(s.input.name)));
        let suffix = 2;
        while (existing.has(`${baseName} ${suffix}`)) suffix += 1;
        const newBaseName = `${baseName} ${suffix}`;
        const newRows = sourceRows.map((s, i) => {
          const oldName = s.input.name;
          const newName = oldName.startsWith(baseName) ? newBaseName + oldName.slice(baseName.length) : oldName;
          return { ...s, id: demoId(), clientId: newClientId, rootClientId: newClientId, input: { ...s.input, name: newName }, createdAt: stamp, updatedAt: stamp, sortOrder: i };
        });
        setDemoScenarios(prev => [...prev, ...newRows]);
        return newClientId;
      }
      // Impersonation: scan every React Query cache for any TaxScenario[] that
      // contains the source clientId, then clone in EVERY such cache. A client
      // owned by the impersonated user lives in both their personal cache AND
      // the org-scenarios cache; if we only clone in the first one found, the
      // currently-rendered view (which may be reading from the other cache)
      // won't see the new row and impersonation appears broken.
      // Mirrors the duplicate_client RPC's auto-increment naming. No DB write.
      if (isImpersonating) {
        const isTaxScenario = (v: unknown): v is TaxScenario =>
          !!v && typeof v === 'object'
            && typeof (v as { clientId?: unknown }).clientId === 'string'
            && !!(v as { input?: { name?: unknown } }).input
            && typeof (v as { input: { name?: unknown } }).input.name === 'string';

        const candidateQueries = queryClient.getQueryCache().getAll().filter(q => {
          const data = q.state.data;
          return Array.isArray(data) && data.length > 0 && isTaxScenario(data[0]);
        });

        // Collect every cache that holds the source — not just the first.
        const matchingCaches: { key: readonly unknown[]; data: TaxScenario[] }[] = [];
        for (const q of candidateQueries) {
          const data = q.state.data as TaxScenario[];
          if (data.some(s => s.clientId === clientId)) {
            matchingCaches.push({ key: q.queryKey, data });
          }
        }

        if (matchingCaches.length === 0) {
          throw new Error(`Source client ${clientId} not in any cached scenarios. Tried ${candidateQueries.length} cache(s).`);
        }

        // Use the first match's data for naming (the row set is identical
        // across caches because they all came from the same DB query result).
        const primary = matchingCaches[0];
        const sourceRows = primary.data.filter(s =>
          s.clientId === clientId && (!baselineOnly || (s.sortOrder ?? 0) === 0));
        const baseline = sourceRows.find(s => (s.sortOrder ?? 0) === 0) ?? sourceRows[0];
        const oldBaselineName = baseline.input.name;
        const root = oldBaselineName.replace(/\s+\d+$/, '');

        // Numbering pool = union of all baseline names across every matching
        // cache, so the suffix doesn't collide with names visible in any view.
        const existing = new Set<string>();
        for (const { data } of matchingCaches) {
          for (const s of data) {
            if ((s.sortOrder ?? 0) === 0) existing.add(s.input.name);
          }
        }
        let suffix = 2;
        while (existing.has(`${root} ${suffix}`)) suffix += 1;
        const newBaselineName = `${root} ${suffix}`;

        const newClientId = `sandbox-client-${Date.now()}`;
        const stamp = new Date().toISOString();
        const newRows: TaxScenario[] = sourceRows.map((s, i) => {
          const oldName = s.input.name;
          const newName = oldName.startsWith(oldBaselineName)
            ? newBaselineName + oldName.slice(oldBaselineName.length)
            : oldName;
          return {
            ...s,
            id: `sandbox-${Date.now()}-${i}`,
            clientId: newClientId,
            rootClientId: s.rootClientId ?? s.clientId,
            input: { ...s.input, name: newName },
            createdAt: stamp,
            updatedAt: stamp,
          };
        });

        // Append to every cache that originally held the source.
        for (const { key } of matchingCaches) {
          queryClient.setQueryData<TaxScenario[]>(
            key as Parameters<typeof queryClient.setQueryData>[0],
            (old: TaxScenario[] | undefined) => [...(old ?? []), ...newRows],
          );
        }
        return newClientId;
      }

      const { duplicateClient } = await import('@/tax/scenarioStore');
      return duplicateClient(clientId, baselineOnly);
    },
    onSuccess: async (newClientId, vars) => {
      logAudit('client.duplicated', 'client', newClientId, {
        source_client_id: vars.clientId,
        source_name: vars.clientName,
      });
      toast.success(`Duplicated "${vars.clientName}"${isImpersonating ? ' (sandbox)' : ''}`);
      if (!isImpersonating) {
        // Wait for the refetch so the copy exists in cache before we open it —
        // otherwise the stranded-view guard sees an unknown client and bounces.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['scenarios'] }),
          queryClient.invalidateQueries({ queryKey: ['org-scenarios'] }),
        ]);
      }
      // Land on the client's versions list with the copy sitting under the
      // original — the same screen you get by opening the folder from the grid.
      // A copy shares the source's lineage, so the list already contains both.
      // The list lives on whichever grid the client was opened from; a
      // teammate's client only exists in the All Clients pool.
      const source = accessibleScenarios.find(s => s.clientId === vars.clientId);
      const rootId = source?.rootClientId ?? vars.clientId;
      const grid = currentView === 'client-view' ? clientViewSourceView : currentView;
      setClientViewId(null);
      setViewedLineageId(rootId);
      setCurrentView(grid === 'org-scenarios' ? 'org-scenarios' : 'dashboard');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  /**
   * Main submit handler — receives base input + array of strategy variants.
   * Creates baseline + one scenario per variant, all sharing the same clientId.
   * Navigates to comparison if variants exist, results if baseline only.
   */
  const handleSubmit = async (payload: WizardSubmitPayload) => {
    // The walkthrough's practice mode: the wizard holds the sample client, so a
    // save only runs the numbers and never persists (Drew, 2026-10-01: all mock
    // data, nothing saved). Guarded to the new-scenario path only — editing a
    // real client mid-walkthrough still saves.
    if (practiceMode && !editingId && !activeDraftId) {
      await runPracticeSubmit(payload);
      return;
    }
    await submitForReal(payload);
  };

  /** Run the real calculation, never persist. Ephemeral scenarios feed the real
   *  Results/Comparison views so a new user can test-drive the full flow:
   *  Save & Calculate lands on the starting point's results (the tour opens the
   *  comparison from there), Save & Compare goes straight to the comparison. */
  const runPracticeSubmit = async (payload: WizardSubmitPayload) => {
    const { baseInput, variants, intent } = payload;
    const clientName = getClientGroup(baseInput.name);
    try {
        const baselineInput: TaxScenarioInput = { ...baseInput, name: clientName || 'Practice Client', alternativeInvestmentAmount: 0 };
        const now = new Date().toISOString();
        const mkScenario = (input: TaxScenarioInput, results: TaxScenario['results'], i: number): TaxScenario => ({
          id: `practice-${i}`,
          clientId: 'practice-client',
          rootClientId: 'practice-client',
          input,
          results,
          createdAt: now,
          updatedAt: now,
        });
        const baselineScenario = mkScenario(baselineInput, await calculateTaxServer(baselineInput), 0);
        const variantScenarios = await Promise.all(
          variants.map(async (v, i) => {
            const variantInput = variantToInput(baseInput, v, clientName || 'Practice Client', variantAutoName(v));
            return mkScenario(variantInput, await calculateTaxServer(variantInput), i + 1);
          })
        );
        const all = [baselineScenario, ...variantScenarios];
        setPracticeScenarios(all);
        setLastResults(baselineScenario);
        setCurrentView(intent === 'compare' && all.length > 1 ? 'compare' : 'results');
    } catch (err: any) {
      toast.error('Error', { description: sanitizeError(err) });
    }
  };

  /** The save path proper — every non-practice submit, and a practice run the
   *  advisor chose to keep. */
  const submitForReal = async (payload: WizardSubmitPayload) => {
    const { baseInput, variants } = payload;
    const clientName = getClientGroup(baseInput.name);

    if (editingId) {
      // Edit path: update baseline, sync variants, then navigate — all sequential
      const editedClientId = editingScenario?.clientId;
      // When an org manager edits an agent's client, new variants must belong to
      // the agent (the baseline owner) — not the logged-in user — so the client's
      // rows stay grouped under one owner.
      const variantOwnerId = editingScenario?.userId ?? user!.id;
      const oldBaseName = getClientGroup(editingScenario?.input.name ?? '');
      try {
        // 1. Update baseline — zero out strategy fields so the baseline stays clean
        const baselineInput: TaxScenarioInput = {
          ...baseInput,
          name: clientName,
          alternativeInvestmentAmount: 0,
        };
        const updatedBaseline = await store.updateScenario(editingId, baselineInput);
        logAudit('scenario.updated', 'scenario', updatedBaseline.id, { scenario_name: updatedBaseline.input.name });

        // 2. If the client name changed, rename existing siblings that haven't been
        //    touched by variant sync yet (pure rename, no recalculation)
        const newBaseName = getClientGroup(updatedBaseline.input.name);
        if (oldBaseName && oldBaseName !== newBaseName) {
          // Use merged pool so agents' sibling variants are renamed too.
          const siblings = accessibleScenarios
            .filter(s => s.clientId === updatedBaseline.clientId && s.id !== updatedBaseline.id);
          // Only rename siblings that are NOT about to be updated/deleted by variant sync
          const variantSyncIds = new Set([
            ...variants.filter(v => v.scenarioId).map(v => v.scenarioId!),
            ...(editingInitialVariants || []).filter(v => v.scenarioId).map(v => v.scenarioId!),
          ]);
          await Promise.all(siblings.filter(s => !variantSyncIds.has(s.id)).map(sibling => {
            const sibBase = getClientGroup(sibling.input.name);
            if (sibBase === oldBaseName) {
              const suffix = sibling.input.name.slice(oldBaseName.length);
              return store.updateScenario(sibling.id, { ...sibling.input, name: newBaseName + suffix });
            }
            return Promise.resolve();
          }));
        }

        // 3. Sync variants: update existing, create new, delete removed
        let syncedVariants: TaxScenario[] = [];
        if (editedClientId) {
          const existingVariants = variants.filter(v => v.scenarioId);
          const newVariants = variants.filter(v => !v.scenarioId);

          const submittedIds = new Set(existingVariants.map(v => v.scenarioId));
          const originalIds = (editingInitialVariants || [])
            .filter(v => v.scenarioId)
            .map(v => v.scenarioId!);
          const removedIds = originalIds.filter(id => !submittedIds.has(id));

          // Re-saving an existing variant must not clobber an advisor's nickname:
          // compare the stored suffix against the auto-name of the config the
          // wizard opened with (editingInitialVariants) — a mismatch means it was
          // renamed on purpose, so that suffix is kept through the baseline edit.
          const initialVariantById = new Map(
            (editingInitialVariants ?? []).filter(iv => iv.scenarioId).map(iv => [iv.scenarioId!, iv] as const)
          );
          const updatePromises = existingVariants.map(v => {
            const freshAutoName = variantAutoName(v);
            const original = initialVariantById.get(v.scenarioId!);
            const stored = accessibleScenarios.find(s => s.id === v.scenarioId);
            const suffix = resolveVariantSuffix(
              stored?.input.name,
              original ? variantAutoName(original) : freshAutoName,
              freshAutoName,
            );
            const variantInput = variantToInput(baselineInput, v, clientName, suffix);
            return store.updateScenario(v.scenarioId!, variantInput).then(s => {
              logAudit('scenario.updated', 'scenario', s.id, { scenario_name: s.input.name });
              return s;
            });
          });

          const createPromises = newVariants.map(v => {
            const suffix = variantAutoName(v);
            const variantInput = variantToInput(baselineInput, v, clientName, suffix);
            return store.createScenario(variantInput, variantOwnerId, editedClientId).then(s => {
              logAudit('scenario.created', 'scenario', s.id, { scenario_name: s.input.name });
              return s;
            });
          });

          const deletePromises = removedIds.map(id =>
            store.deleteScenario(id).then(() => {
              logAudit('scenario.deleted', 'scenario', id, {});
            })
          );

          const [updated, created] = await Promise.all([
            Promise.all(updatePromises),
            Promise.all(createPromises),
            Promise.all(deletePromises),
          ]);
          syncedVariants = [...updated, ...created];
        }

        // 4. Finalize: invalidate cache, navigate
        queryClient.invalidateQueries({ queryKey: ['scenarios'] });
        queryClient.invalidateQueries({ queryKey: ['org-scenarios'] });
        setLastResults(updatedBaseline);
        setEditingId(null);

        if (payload.intent === 'compare' && syncedVariants.length > 0) {
          // Prime cache so ComparisonTable finds the synced scenarios immediately
          const allScenarios = [updatedBaseline, ...syncedVariants];
          const ids = new Set(allScenarios.map(s => s.id));
          queryClient.setQueryData<TaxScenario[]>(scenariosKey, old => [
            ...allScenarios,
            ...((old ?? []).filter(s => !ids.has(s.id))),
          ]);
          setCompareIds(ids);
          setCompareBaselineId(updatedBaseline.id);
          setCurrentView('compare');
        } else if (variants.length > 0) {
          // Auto-route back to the list the edit was launched from (My Clients,
          // All Clients, or Client View), restoring the scroll position the user
          // was at when they opened this client so they land on the same row.
          goBackTo(scopedReturnView);
        } else {
          setCurrentView('results');
        }
      } catch (err: any) {
        toast.error('Error', { description: sanitizeError(err) });
      }
      return;
    }

    if (activeDraftId) {
      // Finalize draft as baseline, then create variants
      try {
        const baselineInput: TaxScenarioInput = { ...baseInput, name: clientName, alternativeInvestmentAmount: 0 };
        const baselineScenario = await store.finalizeDraft(activeDraftId, baselineInput);
        logAudit('scenario.created', 'scenario', baselineScenario.id, { scenario_name: baselineScenario.input.name });

        const variantScenarios = await Promise.all(
          variants.map(v => {
            const suffix = variantAutoName(v);
            const variantInput = variantToInput(baseInput, v, clientName, suffix);
            return store.createScenario(variantInput, user!.id, baselineScenario.clientId).then(s => {
              logAudit('scenario.created', 'scenario', s.id, { scenario_name: s.input.name });
              return s;
            });
          })
        );

        queryClient.invalidateQueries({ queryKey: ['scenarios'] });
        queryClient.invalidateQueries({ queryKey: ['drafts'] });
        queryClient.invalidateQueries({ queryKey: ['org-scenarios'] });
        clearDraft();
        setDraftInitial(null);
        setActiveDraftId(null);

        const allScenarios = [baselineScenario, ...variantScenarios];
        // Optimistically add new scenarios to cache so ComparisonTable finds them immediately
        queryClient.setQueryData<TaxScenario[]>(scenariosKey, old => [
          ...allScenarios,
          ...(old ?? []),
        ]);
        focusNewClient(baselineScenario.clientId);
        if (allScenarios.length === 1) {
          setLastResults(baselineScenario);
          setCurrentView('results');
        } else {
          setCompareIds(new Set(allScenarios.map(s => s.id)));
          setCompareBaselineId(baselineScenario.id);
          setCurrentView('compare');
        }
      } catch (err: any) {
        toast.error('Error', { description: sanitizeError(err) });
      }
      return;
    }

    // Normal create path
    try {
      // Advisor-picked creation (All Clients → New Client): rows are owned by
      // the picked org member; RLS's "Org management insert org scenarios"
      // policy authorizes the cross-owner insert for org owners/admins.
      const ownerId = createForUserId ?? user!.id;
      const baselineInput: TaxScenarioInput = { ...baseInput, name: clientName, alternativeInvestmentAmount: 0 };
      const baselineScenario = await store.createScenario(baselineInput, ownerId);
      logAudit('scenario.created', 'scenario', baselineScenario.id, { scenario_name: baselineScenario.input.name });

      if (!store.isSandbox && assignedClientIds?.has(baselineScenario.clientId)) {
        try { await supabase.rpc('claim_assigned_client', { p_client_id: baselineScenario.clientId }); } catch { /* best-effort: the save already landed */ }
        queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
        logAudit('client.claimed', 'client', baselineScenario.clientId, { client_name: clientName });
      }

      const variantScenarios = await Promise.all(
        variants.map(v => {
          const suffix = variantAutoName(v);
          const variantInput = variantToInput(baseInput, v, clientName, suffix);
          return store.createScenario(variantInput, ownerId, baselineScenario.clientId).then(s => {
            logAudit('scenario.created', 'scenario', s.id, { scenario_name: s.input.name });
            return s;
          });
        })
      );

      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      queryClient.invalidateQueries({ queryKey: ['org-scenarios'] });
      clearDraft();
      setEditingId(null);
      setCreateForUserId(null);

      const allScenarios = [baselineScenario, ...variantScenarios];
      // Optimistically add new scenarios to cache so ComparisonTable finds them
      // immediately. Self-owned rows prime the user-scoped cache; rows created
      // FOR someone else must never enter "My Work" — prime the org pool
      // instead (comparedScenarios reads the merged accessibleScenarios).
      if (createForUserId) {
        queryClient.setQueryData<TaxScenario[]>(['org-scenarios', org?.id], old => [
          ...allScenarios,
          ...((old ?? []).filter(s => !allScenarios.some(n => n.id === s.id))),
        ]);
      } else {
        queryClient.setQueryData<TaxScenario[]>(scenariosKey, old => [
          ...allScenarios,
          ...(old ?? []),
        ]);
      }
      // Land on the new client's folder from the results page. A client created
      // FOR another advisor lives in All Clients, not My Clients — route Back there.
      focusNewClient(baselineScenario.clientId);
      if (createForUserId) setClientViewSourceView('org-scenarios');
      if (allScenarios.length === 1) {
        setLastResults(baselineScenario);
        setCurrentView('results');
      } else {
        setCompareIds(new Set(allScenarios.map(s => s.id)));
        setCompareBaselineId(baselineScenario.id);
        setCurrentView('compare');
      }
    } catch (err: any) {
      toast.error('Error', { description: sanitizeError(err) });
    }
  };

  const handleAutoSaveDraft = async (input: TaxScenarioInput, step: number) => {
    if (!user || autoSavingRef.current) return;
    autoSavingRef.current = true;
    try {
      const inputWithStep = { ...input, _wizardStep: step } as any;
      if (activeDraftId) {
        await store.updateDraftScenario(activeDraftId, inputWithStep);
      } else {
        const draft = await store.saveDraftScenario(inputWithStep, user.id, undefined);
        // Neither of these may remount ScenarioWizard — this resolves whenever
        // the network says so, and `input` here is a snapshot from whenever the
        // step changed, so a remount would roll the user's typing back. The
        // wizard's key is `wizardSessionId`, which these do not touch.
        setDraftInitial(input);
        setActiveDraftId(draft.id);
      }
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
    } catch (err: any) {
      console.error('Auto-save draft failed:', err);
    } finally {
      autoSavingRef.current = false;
    }
  };

  const handleResumeDraft = (draft: TaxScenario) => {
    beginWizardSession();
    setEditingId(null);
    setActiveDraftId(draft.id);
    setDraftInitial(draft.input);
    const savedStep = Math.max(0, Math.min((draft.input as any)._wizardStep ?? 0, WIZARD_STEPS.length - 1));
    setWizardStep(savedStep);
    const visited = new Set<number>();
    for (let i = 0; i < savedStep; i++) visited.add(i);
    setVisitedSteps(visited);
    setCurrentView('wizard');
  };

  const handleEdit = (id: string) => {
    // Baseline is the first row in the client group when sorted by (sortOrder, createdAt)
    // — same comparator DashboardView uses. If the clicked row isn't the baseline, it's a
    // variant and must be edited in isolation via AddStrategySheet so we don't write back
    // to the baseline row.
    // Use the merged pool so org owners can edit agents' clients loaded from "All Clients".
    const clicked = accessibleScenarios.find(s => s.id === id);
    if (!clicked) return;
    const group = accessibleScenarios
      .filter(s => s.clientId === clicked.clientId)
      .sort((a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.createdAt.localeCompare(b.createdAt)
      );
    const baseline = group[0];
    if (baseline && baseline.id !== id) {
      // Variant edit → slide-over
      setEditingVariant({ scenario: clicked, baseline });
      return;
    }
    // Baseline edit → wizard
    beginWizardSession();
    setEditingId(id);
    setActiveDraftId(null);
    setWizardStep(0);
    setVisitedSteps(new Set());
    setCurrentView('wizard');
  };

  // Made-up clients for a walkthrough that would otherwise land on an empty page
  // (Drew, 2026-09-30: "if they don't save anybody, they don't get to see the
  // comparison page"). Calculated for real, held in the demo store so every
  // button works on them, and removed when the walkthrough ends.
  const seedSampleClients = async (): Promise<TaxScenario[] | null> => {
    const id = toast.loading('Setting up sample clients…');
    try {
      const rows = await buildSampleClients(calculateTaxServer, user?.id);
      setDemoScenarios(rows);
      setDemoClientId(null);
      setSampleClientsActive(true);
      toast.dismiss(id);
      return rows;
    } catch (err) {
      toast.dismiss(id);
      toast.error('Could not set up the sample clients', { description: sanitizeError(err) });
      return null;
    }
  };
  const clearSampleClients = () => {
    setDemoScenarios([]);
    setDemoClientId(null);
    setSampleClientsActive(false);
    setClientViewId(null);
    setViewedLineageId(null);
    setCurrentView('dashboard');
  };
  const handleWalkthroughEnd = (_wt: Walkthrough, continuing: boolean) => {
    if (!sampleClientsActive) return;
    clearSampleClients();
    if (!continuing) toast.success('Sample clients removed', { description: 'Click New Client to add your first real one.' });
  };
  // The results page has no button to the comparison, so the results module
  // ends on a "Got it" and the tour opens it from here.
  const handleModuleEnd = (moduleId: string) => {
    if (moduleId === 'results' && practiceMode && practiceScenarios.length > 1) setCurrentView('compare');
  };

  // The full tour for this person: Downline only for those who can open it
  // (its route allows owners, org admins and platform admins).
  const fullTourIds = React.useMemo(
    () => fullTourFor(WALKTHROUGHS.filter(w => walkthroughVisible(w.requires, {
      canSeeDownline: isOrgOwner || isOrgAdmin || isPlatformAdmin,
      noClientBook,
    }))),
    [isOrgOwner, isOrgAdmin, isPlatformAdmin, noClientBook],
  );

  const byClientOrder = (a: TaxScenario, b: TaxScenario) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.createdAt.localeCompare(b.createdAt);

  // Settings → Walkthroughs → Start, or the full tour moving on: put the person
  // on the walkthrough's first surface, with its sample data up (Drew,
  // 2026-10-01: every walkthrough runs on mock data, whatever the person has of
  // their own); the tutorial sequencer takes it from there.
  const handleStartWalkthrough = async (wt: Walkthrough) => {
    switch (wt.start) {
      case 'practice':
        setPracticeMode(true);
        setCurrentView('dashboard');
        return;
      case 'dashboard':
        // Coming off the practice run in the full tour: leave it behind first.
        if (practiceMode) exitPractice();
        else setPracticeMode(false);
        if (!(await seedSampleClients())) return;
        setCurrentView('dashboard');
        return;
      case 'all-clients':
        // A support admin's home: the same sample clients, on All Clients.
        if (practiceMode) exitPractice();
        else setPracticeMode(false);
        if (!(await seedSampleClients())) return;
        setCurrentView('org-scenarios');
        return;
      case 'compare': {
        // The first sample client, ideas and all: baseline first, then the
        // rest in dashboard order.
        const rows = await seedSampleClients();
        if (!rows) return;
        const pick = rows.filter(s => s.clientId === rows[0].clientId);
        pick.sort(byClientOrder);
        setPracticeMode(false);
        handleCompareGroup(pick.map(s => s.id));
        return;
      }
      case 'downline':
        navigate('/team-management');
        return;
    }
  };

  const handleCompareGroup = (ids: string[]) => {
    // Trust the order the dashboard passes in — the user may have reordered
    // rows and/or deselected some scenarios. First ID becomes the baseline.
    setCompareIds(new Set(ids));
    setCompareOrder(ids);
    setCompareBaselineId(ids[0] || null);
    setCurrentView('compare');
  };

  const handleNewScenario = () => {
    beginWizardSession();
    setEditingId(null);
    setActiveDraftId(null);
    setWizardStep(0);
    setVisitedSteps(new Set());
    setDraftInitial(null);
    setCreateForUserId(null);
    // A brand-new client doesn't belong to any current lineage being viewed —
    // clear so the post-save dashboard return lands on the grid.
    setViewedLineageId(null);
    setCurrentView('wizard');
  };

  /** All Clients → New Client with an advisor chosen: same fresh-wizard reset,
   * but the eventual save is owned by the picked org member. Picking yourself
   * degrades to a normal self-owned create. */
  const handleNewClientFor = (targetUserId: string) => {
    handleNewScenario();
    if (targetUserId && targetUserId !== user?.id) {
      setCreateForUserId(targetUserId);
    }
  };

  /** Open the add-strategy slide-over for a given client group */
  const handleAddScenario = (scenario: TaxScenario) => {
    // Match DashboardView.tsx:74-76 so the baseline we anchor on is the same row the user sees in slot 1.
    const groupScenarios = accessibleScenarios
      .filter(s => s.clientId === scenario.clientId)
      .sort((a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.createdAt.localeCompare(b.createdAt)
      );
    const baseline = groupScenarios[0] || scenario;
    setAddStrategyBaseline(baseline);
    // Pre-stage the compare view's scenario set so it's ready when (and if) the
    // user actually saves a variant. Don't switch the view yet — the slide-over
    // is a modal, so the user should stay on whatever screen they came from.
    // Cancelling the slide-over leaves them where they were.
    const groupIds = groupScenarios.map(s => s.id);
    setCompareIds(new Set(groupIds));
    setCompareOrder(groupIds);
    setCompareBaselineId(baseline.id);
  };

  /** Called by AddStrategySheet when a variant scenario is added or edited */
  const handleVariantSaved = (saved: TaxScenario) => {
    const wasEditing = !!editingVariant;
    setAddStrategyBaseline(null);
    setEditingVariant(null);
    queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    queryClient.invalidateQueries({ queryKey: ['org-scenarios'] });
    if (wasEditing) {
      logAudit('scenario.updated', 'scenario', saved.id, { scenario_name: saved.input.name });
    } else {
      setCompareIds(prev => new Set([...prev, saved.id]));
      setCompareOrder(prev => (prev.includes(saved.id) ? prev : [...prev, saved.id]));
      logAudit('scenario.created', 'scenario', saved.id, { scenario_name: saved.input.name });
      // Show the user the comparison they just added to.
      setCurrentView('compare');
    }
  };

  const handleWizardStepChange = (newStep: number) => {
    const clamped = Math.max(0, Math.min(newStep, WIZARD_STEPS.length - 1));
    if (clamped > wizardStep) setVisitedSteps(prev => new Set(prev).add(wizardStep));
    setWizardStep(clamped);
  };

  const handleViewResults = (s: TaxScenario) => {
    setLastResults(s);
    setCurrentView('results');
  };

  const editingScenario = accessibleScenarios.find(s => s.id === editingId);

  // Reconstruct strategy variants from sibling scenarios when editing the baseline.
  // Shared helper ensures the wizard and AddStrategySheet read stored variants the same way.
  const editingInitialVariants: StrategyVariant[] | undefined = React.useMemo(() => {
    if (!editingScenario) return undefined;
    const siblings = accessibleScenarios.filter(
      s => s.clientId === editingScenario.clientId && s.id !== editingId
    );
    if (siblings.length === 0) return undefined;
    return siblings.map(s => scenarioToVariant(s, editingScenario));
  }, [editingId, editingScenario, accessibleScenarios]);
  const comparedScenarios = (() => {
    const byId = new Map(accessibleScenarios.map(s => [s.id, s] as const));
    // Walk compareOrder first so the user's chosen order is honored.
    const ordered: TaxScenario[] = [];
    const seen = new Set<string>();
    for (const id of compareOrder) {
      if (!compareIds.has(id)) continue;
      const s = byId.get(id);
      if (s) {
        ordered.push(s);
        seen.add(id);
      }
    }
    // Append any compareIds that weren't in compareOrder yet (e.g. just-added strategies).
    for (const id of compareIds) {
      if (seen.has(id)) continue;
      const s = byId.get(id);
      if (s) ordered.push(s);
    }
    return ordered;
  })();

  const isNewScenario = !editingId;

  const renderContent = () => {
    if (isLoading && (currentView === 'dashboard' || currentView === 'client-view' || currentView === 'wizard' || currentView === 'results' || currentView === 'compare')) {
      return <div className="text-center py-16 text-muted-foreground">Loading scenarios…</div>;
    }

    switch (currentView) {
      case 'wizard':
        return (
          <div className="max-w-5xl">
            <ScenarioWizard
              key={wizardSessionId}
              // The walkthrough opens the wizard on the sample client, idea and
              // all, so there is nothing to type (Drew, 2026-10-01).
              initial={editingScenario?.input || draftInitial || (practiceMode ? SAMPLE_WIZARD_CLIENT : undefined)}
              initialVariants={editingInitialVariants ?? (practiceMode && !editingId ? sampleWizardVariants() : undefined)}
              onSubmit={handleSubmit}
              // No auto-drafts when creating for another advisor: drafts are
              // owner-scoped, and a draft owned by the creator would strand a
              // half-finished client in the wrong person's Drafts list.
              onAutoSaveDraft={isNewScenario && !practiceMode && !createForUserId ? handleAutoSaveDraft : undefined}
              currentStep={wizardStep}
              onStepChange={handleWizardStepChange}
              visitedSteps={visitedSteps}
              isNewScenario={isNewScenario}
              submitRef={wizardSubmitRef}
              submitCompareRef={wizardSubmitCompareRef}
              strategiesReadOnly={!!editingId}
            />
          </div>
        );
      case 'results':
        return lastResults ? (
          <ResultsDisplay
            results={lastResults.results}
            input={lastResults.input}
            scenarioName={lastResults.input.name}
            onCreateScenario={practiceMode ? undefined : () => handleAddScenario(lastResults)}
            onBack={() => goBackTo(scopedReturnView)}
          />
        ) : (
          <div className="text-center py-16 text-muted-foreground">No results yet. Create a scenario first.</div>
        );
      case 'compare':
        // Practice-mode comparisons render from the ephemeral in-memory
        // scenarios — nothing was saved, so the query-backed pool has none.
        if (practiceMode && practiceScenarios.length > 1) {
          return (
            <ComparisonTable
              scenarios={practiceScenarios}
              onBack={exitPractice}
            />
          );
        }
        return (
          <ComparisonTable
            scenarios={comparedScenarios}
            onAddStrategy={handleAddScenario}
            onBack={() => goBackTo(scopedReturnView)}
            onReorder={(newOrder) => {
              setCompareOrder(newOrder);
              setCompareBaselineId(newOrder[0] ?? null);
            }}
          />
        );
      case 'team':
        return <TeamManagement />;
      case 'org-scenarios': {
        // Lineage row-list in org context: clicked a multi-version folder from
        // All Clients. Render DashboardView filtered to that lineage's
        // scenarios so Compare / Add / Duplicate / Client View buttons are
        // immediately reachable.
        if (viewedLineageId) {
          const lineageScenarios = orgScenarios.filter(s => s.rootClientId === viewedLineageId);
          const lineageName = lineageScenarios.find(s => (s.sortOrder ?? 0) === 0 && s.clientId === viewedLineageId)?.input.name
            ?? lineageScenarios[0]?.input.name
            ?? 'Lineage';
          return (
            <div className="space-y-3">
              <h2 className="text-xl font-semibold truncate">{lineageName} · {new Set(lineageScenarios.map(s => s.clientId)).size} versions</h2>
              <DashboardView
                scenarios={lineageScenarios}
                drafts={[]}
                isAdmin={isSuperAdmin}
                canDeleteAll={isSuperAdmin || isOrgOwner || isOrgAdmin}
                onEdit={handleEdit}
                onDuplicate={s => dupMut.mutate(s)}
                onDelete={handleDelete}
                onDeleteMultiple={handleDeleteMultiple}
                onViewResults={handleViewResults}
                onNewScenario={handleNewScenario}
                onAddScenario={handleAddScenario}
                onCompareGroup={handleCompareGroup}
                onResumeDraft={handleResumeDraft}
                onDeleteDraft={handleDelete}
                onDuplicateClient={(clientId, clientName, baselineOnly) => dupClientMut.mutate({ clientId, clientName, baselineOnly })}
                onClientView={handleClientView}
                onRenameScenario={(scenario, newName) => renameMut.mutate({ scenario, newName })}
                isOrgView
                creatorById={creatorById}
              />
            </div>
          );
        }
        return (
          <MyClientsGrid
            scenarios={visibleOrgScenarios}
            drafts={[]}
            onNewScenario={handleNewScenario}
            onClientView={handleClientView}
            onLineageOpen={(rootClientId) => setViewedLineageId(rootClientId)}
            isOrgView
            creatorById={creatorById}
            advisors={practiceMode ? sampleAdvisors() : activeAdvisors.filter(p => carriesClientBook(p.has_client_book)).map(p => ({ id: p.id, label: p.full_name?.trim() || p.email || 'Unknown' }))}
            onNewClientFor={handleNewClientFor}
          />
        );
      }
      case 'client-view': {
        // Merged pool, not the user-scoped list: Client View must render a
        // teammate's client opened from All Clients, not just your own.
        // Reorder persistence stays owner-only — reorder_scenarios RAISEs on
        // rows the caller doesn't own, so don't offer the drag on a
        // teammate's client.
        const viewedClientIsMine = accessibleScenarios.some(
          s => s.clientId === clientViewId && s.userId === user?.id,
        );
        return (
          <DashboardView
            scenarios={accessibleScenarios}
            drafts={drafts}
            isAdmin={isSuperAdmin}
            canDeleteAll={isSuperAdmin || isOrgOwner || isOrgAdmin}
            onEdit={handleEdit}
            onDuplicate={s => dupMut.mutate(s)}
            onDelete={handleDelete}
            onDeleteMultiple={handleDeleteMultiple}
            onViewResults={handleViewResults}
            onNewScenario={handleNewScenario}
            onAddScenario={handleAddScenario}
            onCompareGroup={handleCompareGroup}
            onResumeDraft={handleResumeDraft}
            onDeleteDraft={handleDelete}
            onReorderPersist={viewedClientIsMine ? (ids, orders) => reorderMut.mutate({ ids, orders }) : undefined}
            onDuplicateClient={(clientId, clientName, baselineOnly) => dupClientMut.mutate({ clientId, clientName, baselineOnly })}
            onRenameScenario={(scenario, newName) => renameMut.mutate({ scenario, newName })}
            singleClientId={clientViewId ?? undefined}
          />
        );
      }
      case 'assignments':
        return <CaseAssignments />;
      case 'settings':
        return <SettingsPage />;
      case 'drafts':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Button size="sm" variant="ghost" onClick={() => setCurrentView('dashboard')} className="h-8 px-2 -ml-1">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back to My Clients
              </Button>
              <h2 className="text-xl font-semibold">Drafts ({drafts.length})</h2>
            </div>
            {drafts.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-12 border border-dashed rounded-lg">
                No drafts in progress. Start a new client to create one.
              </div>
            ) : (
              <div className="border rounded-lg divide-y bg-card">
                {drafts.map(d => (
                  <div key={d.id} className="flex items-center justify-between gap-2 px-4 py-3 hover:bg-muted/50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{d.input.name || 'Untitled Draft'}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Roth {formatCompactCurrency(d.input.rothConversionAmount)} · Alt {formatCompactCurrency(d.input.alternativeInvestmentAmount)} · LTCG {formatCompactCurrency(d.input.longTermCapitalGains)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleResumeDraft(d)}>
                        <Play className="h-3 w-3 mr-1" /> Resume
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => handleDelete(d.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'dashboard':
      default: {
        // Lineage row-list: clicked a multi-version folder. Render the existing
        // DashboardView filtered to that lineage so Compare / Add / Duplicate
        // buttons are immediately available.
        if (viewedLineageId) {
          const lineageScenarios = scenarios.filter(s => s.rootClientId === viewedLineageId);
          const lineageDrafts = drafts.filter(d => d.rootClientId === viewedLineageId);
          const lineageName = lineageScenarios.find(s => (s.sortOrder ?? 0) === 0 && s.clientId === viewedLineageId)?.input.name
            ?? lineageScenarios[0]?.input.name
            ?? 'Lineage';
          return (
            <div className="space-y-3">
              <h2 className="text-xl font-semibold truncate">{lineageName} · {new Set(lineageScenarios.map(s => s.clientId)).size} versions</h2>
              <DashboardView
                scenarios={lineageScenarios}
                drafts={lineageDrafts}
                isAdmin={isSuperAdmin}
                canDeleteAll={isSuperAdmin || isOrgOwner || isOrgAdmin}
                onEdit={handleEdit}
                onDuplicate={s => dupMut.mutate(s)}
                onDelete={handleDelete}
                onDeleteMultiple={handleDeleteMultiple}
                onViewResults={handleViewResults}
                onNewScenario={handleNewScenario}
                onAddScenario={handleAddScenario}
                onCompareGroup={handleCompareGroup}
                onResumeDraft={handleResumeDraft}
                onDeleteDraft={handleDelete}
                onReorderPersist={(ids, orders) => reorderMut.mutate({ ids, orders })}
                onDuplicateClient={(clientId, clientName, baselineOnly) => dupClientMut.mutate({ clientId, clientName, baselineOnly })}
                onClientView={handleClientView}
                onRenameScenario={(scenario, newName) => renameMut.mutate({ scenario, newName })}
                expandedClients={expandedClients}
                onToggleExpand={toggleClientExpanded}
              />
            </div>
          );
        }
        return (
          <MyClientsGrid
            scenarios={scenarios}
            drafts={drafts}
            onNewScenario={handleNewScenario}
            onClientView={handleClientView}
            onLineageOpen={(rootClientId) => setViewedLineageId(rootClientId)}
            onMergeLineages={(keep, drops) => mergeLineagesMut.mutateAsync({ keepRootId: keep, dropRootIds: drops })}
            onDeleteLineages={handleDeleteLineages}
          />
        );
      }
    }
  };

  return (
    <TutorialProvider
      view={currentView}
      wizardStep={wizardStep}
      fullTourIds={fullTourIds}
      onStartWalkthrough={handleStartWalkthrough}
      onWalkthroughEnd={handleWalkthroughEnd}
      onModuleEnd={handleModuleEnd}
    >
    <AppShell
      headerSlot={(() => {
        // Sticky "← Back to Dashboard • {client name}" — shown only when the
        // current view is scoped to a single client (results or comparison).
        let clientName: string | null = null;
        if (currentView === 'results' && lastResults) {
          clientName = getClientGroup(lastResults.input.name);
        } else if (currentView === 'compare' && comparedScenarios.length > 0) {
          clientName = getClientGroup(comparedScenarios[0].input.name);
        } else if (currentView === 'client-view' && clientViewId) {
          const s = scenarios.find(sc => sc.clientId === clientViewId);
          if (s) clientName = getClientGroup(s.input.name);
        }
        if (!clientName) return null;
        // Client View is home base: once an advisor opens a client folder, that
        // folder is as far back as any in-client page (results, compare, edit)
        // goes, and the folder itself has no Back — the sidebar is the only way
        // out, so a presentation never surfaces a "Back to My Clients" link.
        // The one exception is a demo client opened from Team Management, which
        // is a different route and needs a way home.
        if (currentView === 'client-view' && (!isDemoMode || sampleClientsActive)) {
          return <span className="font-semibold truncate">{clientName}</span>;
        }
        const backTarget: AppView = currentView === 'client-view' ? clientViewSourceView : scopedReturnView;
        let backLabel = 'Back to My Clients';
        if (isDemoMode && !sampleClientsActive && currentView === 'client-view') {
          backLabel = 'Back to Downline Management';
        } else if (backTarget === 'client-view') {
          backLabel = 'Back to Client View';
        } else if (backTarget === 'org-scenarios') {
          if (viewedLineageId) {
            const lineageHead = orgScenarios.find(s => s.clientId === viewedLineageId)?.input.name
              ?? orgScenarios.find(s => s.rootClientId === viewedLineageId)?.input.name
              ?? 'lineage';
            backLabel = `Back to ${lineageHead}`;
          } else {
            backLabel = 'Back to All Clients';
          }
        } else if (backTarget === 'dashboard' && viewedLineageId) {
          const lineageHead = scenarios.find(s => s.clientId === viewedLineageId)?.input.name
            ?? scenarios.find(s => s.rootClientId === viewedLineageId)?.input.name
            ?? 'lineage';
          backLabel = `Back to ${lineageHead}`;
        }
        return (
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              data-tour="back-link"
              onClick={() => {
                if (isDemoMode && !sampleClientsActive && currentView === 'client-view') {
                  setDemoScenarios([]);
                  setDemoClientId(null);
                  navigate('/team-management');
                } else {
                  goBackTo(backTarget);
                }
              }}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {backLabel}
            </Button>
            <span className="text-muted-foreground/50" aria-hidden>•</span>
            <span className="font-semibold truncate">{clientName}</span>
          </div>
        );
      })()}
      sidebar={
        <AppSidebar
          currentView={currentView}
          wizardStep={wizardStep}
          visitedSteps={visitedSteps}
          onViewChange={(view) => {
            // Clean up wizard state when navigating away from wizard
            if (view !== 'wizard' && currentView === 'wizard') {
              setEditingId(null);
              setActiveDraftId(null);
              setDraftInitial(null);
              setLastResults(null);
              setWizardStep(0);
              setVisitedSteps(new Set());
            }
            // Clear stale results when going to dashboard
            if (view === 'dashboard') {
              setLastResults(null);
            }
            // Sidebar nav to either grid view always lands on the grid itself,
            // not a nested lineage row-list left over from earlier navigation.
            if (view === 'dashboard' || view === 'org-scenarios') {
              setViewedLineageId(null);
            }
            // Same-view sidebar click on long pages = scroll to top.
            // Limited to dashboard and org-audit since those are the only
            // views long enough for the gesture to be useful.
            if (view === currentView && (view === 'dashboard' || view === 'settings')) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            setCurrentView(view);
          }}
          onWizardStepChange={handleWizardStepChange}
          onNewScenario={handleNewScenario}
          scenarioCount={new Set(scenarios.map(s => s.clientId)).size}
          draftCount={drafts.length}
          onSaveAndCalculate={() => wizardSubmitRef.current?.()}
          onSaveAndCompare={
            !!editingId && (editingInitialVariants?.length ?? 0) > 0
              ? () => wizardSubmitCompareRef.current?.()
              : undefined
          }
        />
      }
    >
      {practiceMode && (
        <WalkthroughBanner data-tour="practice-banner" action="Exit walkthrough" onAction={exitPractice}>
          <strong>Walkthrough</strong> — nothing you do here is saved.
        </WalkthroughBanner>
      )}
      {sampleClientsActive && (
        <WalkthroughBanner data-tour="sample-banner" action="Remove sample clients" onAction={clearSampleClients}>
          <strong>Sample clients</strong> — {SAMPLE_NOTICE}
        </WalkthroughBanner>
      )}
      {renderContent()}

      {/* Add / Edit Strategy slide-over — a single sheet instance serves both flows.
          `editingVariant` drives edit mode; `addStrategyBaseline` drives add mode. */}
      <AddStrategySheet
        key={editingVariant?.scenario.id ?? 'add'}
        baseline={editingVariant?.baseline ?? addStrategyBaseline}
        allClientScenarios={(() => {
          const cid = editingVariant?.baseline.clientId ?? addStrategyBaseline?.clientId;
          // Pull from merged pool so an org manager adding a variant to an agent's
          // client sees the agent's existing variants (not an empty list).
          return cid ? accessibleScenarios.filter(s => s.clientId === cid) : [];
        })()}
        userId={
          // New variants must be owned by the baseline's owner so agent-owned
          // clients stay grouped under the agent, not the logged-in org manager.
          editingVariant?.baseline.userId
          ?? addStrategyBaseline?.userId
          ?? user?.id
          ?? ''
        }
        editingScenario={editingVariant?.scenario ?? null}
        onSaved={handleVariantSaved}
        onClose={() => {
          setAddStrategyBaseline(null);
          setEditingVariant(null);
        }}
      />
    </AppShell>
    </TutorialProvider>
  );
}
